// import { Injectable } from '@angular/core';
// import { BehaviorSubject, Observable } from 'rxjs';

// /**
//  * CHROME SERVICE — single source of truth for app-chrome state shared
//  * between the header (hamburger button) and the vertical nav (sidebar).
//  *
//  * Why this exists: the hamburger lives inside <app-billing-header> and
//  * the sidebar lives inside <app-verticalnav>. They are siblings, not
//  * parent/child, so a property binding can't connect them directly.
//  * This service is the glue.
//  *
//  * Usage:
//  *   constructor(private chrome: ChromeService) {}
//  *
//  *   // header — when hamburger is clicked:
//  *   toggleSidebar() { this.chrome.toggleSidebar(); }
//  *
//  *   // sidebar — in ngOnInit:
//  *   this.chrome.isSidebarOpen$.subscribe(open => this.isOpen = open);
//  *
//  *   // anywhere — close sidebar after navigation:
//  *   this.chrome.closeSidebar();
//  */
// @Injectable({ providedIn: 'root' })
// export class ChromeService {

//   /** Stream the rest of the app subscribes to. */
//   private readonly _isSidebarOpen$ = new BehaviorSubject<boolean>(false);
//   readonly isSidebarOpen$: Observable<boolean> = this._isSidebarOpen$.asObservable();

//   /** Synchronous snapshot — handy inside a click handler. */
//   get isSidebarOpen(): boolean {
//     return this._isSidebarOpen$.value;
//   }

//   toggleSidebar(): void {
//     this._isSidebarOpen$.next(!this._isSidebarOpen$.value);
//     this.syncBodyClass();
//   }

//   openSidebar(): void {
//     if (!this._isSidebarOpen$.value) {
//       this._isSidebarOpen$.next(true);
//       this.syncBodyClass();
//     }
//   }

//   closeSidebar(): void {
//     if (this._isSidebarOpen$.value) {
//       this._isSidebarOpen$.next(false);
//       this.syncBodyClass();
//     }
//   }

//   /**
//    * Mirrors the state onto <body> so global CSS (scroll lock, backdrop)
//    * can react without each component re-implementing it.
//    * The class name is namespaced so it can't collide with Bootstrap
//    * helpers or any other library that uses generic class names.
//    */
//   private syncBodyClass(): void {
//     const body = typeof document !== 'undefined' ? document.body : null;
//     if (!body) { return; }
//     body.classList.toggle('app-sidebar-open', this._isSidebarOpen$.value);
//   }
// }


import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * ChromeService — reverse-engineered strictly from usage. Header.ts and VerticalNav.ts (via the
 * "chrome" injected name) call exactly:
 *
 *   chrome.toggleSidebar()        -> void, flips sidebar open/closed
 *   chrome.isSidebarOpen$         -> Observable<boolean>, VerticalNav subscribes to sync `isOpen`
 *   chrome.isSidebarOpen          -> boolean, read synchronously in VerticalNav's document-click handler
 *   chrome.closeSidebar()         -> void
 *
 * No real chrome.service.ts was ever found in this file set (same situation as CoreGymService —
 * see its header comment). This is a minimal, honest stand-in covering exactly that surface: an
 * app-shell "is the mobile sidebar open" toggle, backed by a BehaviorSubject so late subscribers
 * get the current state immediately. If your real app already has a ChromeService, delete this
 * file and drop the real one in at this same path.
 */
@Injectable({ providedIn: 'root' })
export class ChromeService {
  private readonly sidebarOpen = new BehaviorSubject<boolean>(false);

  readonly isSidebarOpen$: Observable<boolean> = this.sidebarOpen.asObservable();

  get isSidebarOpen(): boolean { return this.sidebarOpen.value; }

  toggleSidebar(): void { this.sidebarOpen.next(!this.sidebarOpen.value); }
  openSidebar(): void { this.sidebarOpen.next(true); }
  closeSidebar(): void { this.sidebarOpen.next(false); }
}

